import React, {useEffect, useRef, useState} from 'react';
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputPassword from "../../../../components/input/input-password.jsx";
import Alert from "../../../../components/alert/alert.jsx";
import {FaEnvelope} from "react-icons/fa";
import {FaCircleExclamation} from "react-icons/fa6";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import loginService from "../../../../services/web/login-service.js";
import ResetRetryCountdown from "../../../../components/reset-retry-countdown/reset-retry-countdown.js";
import HandleHashInput from "../../../../components/handle-hash/input.js";
import HandleHashPaste from "../../../../components/handle-hash/paste.js";
import {translateDestinationMessage, translateServerErrorMessage} from "../utils/login-utils.js";
import InputRadio from "../../../../components/input/input-radio.jsx";

const API_URL = import.meta.env.VITE_API_URL;
const RESEND_SECONDS = 60;

const PasswordRecoveryModal = ({open, setOpen, login, passwordComplexity, onSuccess}) => {
    const navigate = navigateHook();

    const [step, setStep] = useState("choose");
    const [mode, setMode] = useState(null);
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    const hashRef = useRef([]);
    const resendBtn = useRef();
    const resendCode = useRef();
    const timerEl = useRef();
    const timer = useRef();

    const modeOptions = [
        {key: "SMS", title: "SMS", description: "Enviar código por mensagem de texto"},
        {key: "EMAIL", title: "E-mail", description: "Enviar código para o e-mail cadastrado"},
    ];

    useEffect(() => {
        if (open) {
            setStep("choose");
            setMode(null);
            setPassword("");
            setConfirmation("");
            setErrorMessage("");
        }
    }, [open]);

    useEffect(() => {
        return () => {
            if (timer.current) clearInterval(timer.current);
        };
    }, []);

    const openContacts = () => navigate("/contacts", true);

    const handleImageError = (e) => {
        e.currentTarget.onerror = null;
        e.currentTarget.src = getAssetPath("img/sentry-logo3.png");
    };

    const handleRecoveryError = (error) => {
        const message = error.response?.data?.message;

        if (message === "invalid.email" || message === "invalid.phone") {
            projectUtils.swalMessage(
                "warning",
                "Não foi possível iniciar o processo",
                "",
                "Contatos",
                false,
                "",
                openContacts,
                true
            );
            return;
        }

        projectUtils.swalErrorMessage("", translateServerErrorMessage(message), "Ok");
    };

    const requestCode = (isResend = false) => {
        if (!mode) {
            setErrorMessage("Selecione um método para prosseguir.");
            return;
        }

        if (!isResend) setLoading(true);

        const formData = new FormData();
        formData.append("login", login);
        formData.append("mode", mode);

        loginService.passwordRecovery(formData)
            .then(() => {
                setErrorMessage("");
                setPassword("");
                setConfirmation("");
                hashRef.current.forEach(el => { if (el) el.value = ""; });
                setStep("code");
                ResetRetryCountdown(resendBtn, resendCode, timerEl, false, timer, RESEND_SECONDS, () => requestCode(true));
            })
            .catch(handleRecoveryError)
            .finally(() => { if (!isResend) setLoading(false); });
    };

    const changePassword = () => {
        const hash = hashRef.current.map(el => el?.value ?? "").join("");
        const cleanedPassword = password.trim();

        if (cleanedPassword !== confirmation) {
            setErrorMessage("A senha e a confirmação devem ser idênticas.");
            return;
        }

        if (!cleanedPassword || !confirmation) {
            setErrorMessage("Preencha os campos obrigatórios.");
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append("login", login);
        formData.append("hash", hash);
        formData.append("password", cleanedPassword);

        loginService.setPassword(formData)
            .then(() => {
                setOpen(false);
                onSuccess();
            })
            .catch(error => {
                console.error(error);

                const message = error.response?.data?.message ?? error.response?.data;

                if (message === "invalid.hash") {
                    setErrorMessage("Código inválido.");
                    hashRef.current.forEach(el => el?.classList.add("border-red-500"));
                    hashRef.current[0]?.focus();
                    hashRef.current[0]?.select();
                } else {
                    setErrorMessage(translateServerErrorMessage(message));
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <Modal
            open={open}
            setOpen={() => setOpen(false)}
            className="w-[480px] max-w-full"
            title={<img src={getAssetPath("img/logo.svg")} alt="Sentry" className="w-[100px]"/>}
        >
            <Modal.Body>
                <div hidden={step !== "choose"}>
                    <h4 className="text-center font-semibold mb-4">Primeiro acesso ou recuperação de senha</h4>

                    <p className="text-[15px] text-gray-700 mb-2 text-center">Escolha como prosseguir</p>

                    <div className="flex flex-col gap-2 items-center w-full">
                        {modeOptions.map(option => (
                            <div
                                key={option.key}
                                onClick={() => setMode(option.key)}
                                className="flex items-center gap-3 rounded-lg px-4 py-6 cursor-pointer bg-[#E6E7EB] w-[290px]"
                            >
                                <InputRadio
                                    id={"password-recovery-" + option.key.toLowerCase()}
                                    name="password-recovery-mode"
                                    checked={mode === option.key}
                                    onChange={() => setMode(option.key)}
                                />
                                <div className="flex flex-col text-start">
                                    <span className="font-semibold text-[15px]">{option.title}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div hidden={step !== "code"}>
                    <h4 className="text-center font-semibold text-[18px] mb-1">Configurar senha</h4>
                    <div className="text-center text-[15px] text-gray-700 mb-4">
                        Insira o código de verificação que foi enviado para o seu {mode === "EMAIL" ? "email" : "celular"}:
                        <br/>
                    </div>

                    <div className="flex justify-center items-center mb-4">
                        <div className="flex flex-row gap-3">
                            {[...Array(6)].map((_, index) => (
                                <input
                                    key={index}
                                    className="bg-white bg-none border border-[#e5e6e7] text-inherit block px-3 py-1.5 uppercase w-[35px] h-[35px] rounded-md"
                                    type="text"
                                    maxLength="1"
                                    required
                                    autoFocus={index === 0}
                                    ref={(el) => hashRef.current[index] = el}
                                    onInput={(e) => HandleHashInput(e, index, hashRef)}
                                    onPaste={(e) => HandleHashPaste(e, hashRef)}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <InputPassword
                            id="new_password"
                            name="new_password"
                            target="password"
                            passwordComplexity={passwordComplexity}
                            label="Nova senha *"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        <InputPassword
                            id="new_password_confirm"
                            name="new_password_confirm"
                            label="Confirmação de senha *"
                            value={confirmation}
                            password={password}
                            onChange={(e) => setConfirmation(e.target.value)}
                        />
                    </div>

                    <span className="flex gap-1 justify-center items-center text-[#063A6C] text-[14px] mt-4" ref={resendBtn}>
                        <FaEnvelope/>
                        Não recebeu o código?
                        <span className="cursor-pointer" ref={resendCode}>Reenviar</span>
                        <span ref={timerEl}/>
                    </span>
                </div>

                {
                    errorMessage &&
                    <Alert target="danger" className="mt-4 flex items-center gap-2 !text-[15px]">
                        <FaCircleExclamation/> {errorMessage}
                    </Alert>
                }
            </Modal.Body>

            <Modal.Footer>
                {
                    step === "choose"
                        ? <Button onClick={() => requestCode(false)} loading={loading} disabled={!mode}>Avançar</Button>
                        : <Button onClick={changePassword} loading={loading}>Definir</Button>
                }
            </Modal.Footer>
        </Modal>
    );
};

export default PasswordRecoveryModal;
